import { Typography } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CloseButton,
  Scene,
  StyledCheckIcon,
  StyledCloseIcon,
} from './teamingNoticeStyles';

import { useAppDispatch } from 'features/app/hooks';
import { requestCognitoForcedLogout } from 'features/app/store/app/appSlice';
import theme from 'features/theme/theme';
import { BaseHeader, MainTemplate } from 'features/ui/elements';

const FORCED_LOGOUT_DELAY_MS = 5_000;

const TeamingLogoutNoticePage: FC = () => {
  const dispatch = useAppDispatch();
  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const hasRequestedLogoutRef = useRef(false);
  const { t: tForced } = useTranslation('translation', {
    keyPrefix: 'forcedLogout',
  });

  const handleRequestLogout = useCallback(() => {
    if (logoutTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(logoutTimeoutRef.current);
    }

    if (hasRequestedLogoutRef.current) {
      return;
    }

    hasRequestedLogoutRef.current = true;
    dispatch(requestCognitoForcedLogout());
  }, [dispatch]);

  useEffect(() => {
    logoutTimeoutRef.current = globalThis.setTimeout(() => {
      handleRequestLogout();
    }, FORCED_LOGOUT_DELAY_MS);

    return () => {
      if (logoutTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, [handleRequestLogout]);

  return (
    <MainTemplate header={<BaseHeader />} isHideBackground>
      <Scene>
        <Card aria-live='polite' role='status'>
          <CloseButton
            aria-label='close-logout-notice'
            title='close-logout-notice'
            type='button'
            onClick={handleRequestLogout}
          >
            <StyledCloseIcon />
          </CloseButton>
          <StyledCheckIcon title='teaming-logout-check-icon' />
          <Typography
            variant='h2'
            color={theme.palette.common.white}
            mt={12}
            textAlign='center'
          >
            {tForced('title')}
          </Typography>
          <Typography
            variant='body1'
            color={theme.palette.grey[100]}
            mt={8}
            textAlign='center'
          >
            {tForced('description')}
          </Typography>
          <Typography
            variant='subtitle2'
            color={theme.palette.grey[300]}
            mt={20}
            textAlign='center'
          >
            {tForced('securityReminder')}
          </Typography>
        </Card>
      </Scene>
    </MainTemplate>
  );
};

export default TeamingLogoutNoticePage;
